// src/components/stages/VitalsStage.tsx
import { useState, useEffect, useMemo } from 'react';
import { authFetch, getAilmentTemplates } from '../../api';
import { useSymptomFields } from '../../features/symptom-form/api';
import { SymptomFormFields } from '../../features/symptom-form/SymptomFormFields';
import {
  buildAnswers, composeSymptomsText, isAnswered, missingMandatory, overLimit, readSnapshot, valuesFromAnswers,
  type SymptomField, type SymptomValue,
} from '../../features/symptom-form/symptomForm';
import VoiceMicButton from '../VoiceMicButton';
import VoiceFormFiller, { type VoiceField } from '../VoiceFormFiller';
import { StatField } from '../ui/StatField';
import { SegmentedControl } from '../ui/SegmentedControl';
import { SectionCard, type SectionStatus } from '../ui/SectionCard';
import { StageActionBar } from '../ui/StageActionBar';
import { VitalsCoreFields, computeBmi } from './VitalsCoreFields';
import type { StageProps } from '../../types/visit';

const VOICE_FIELDS: VoiceField[] = [
  { key:'bloodGroup', label:'Blood group', type:'select', options:['A+','A-','B+','B-','O+','O-','AB+','AB-'] },
  { key:'weight',  label:'Weight in kg',   type:'number' },
  { key:'height',  label:'Height in cm',   type:'number' },
  { key:'bp_sys',  label:'BP systolic',    type:'number' },
  { key:'bp_dia',  label:'BP diastolic',   type:'number' },
  { key:'pulse',   label:'Pulse rate (bpm)', type:'number' },
  { key:'temp',    label:'Temperature (°F)', type:'number' },
  { key:'spo2',    label:'SpO2 (%)',       type:'number' },
  { key:'symptoms', label:'Symptoms / complaints', type:'text' },
];

interface VitalsFormState {
  bloodGroup: string;
  weight: string;
  height: string;
  bp_sys: string;
  bp_dia: string;
  pulse: string;
  temp: string;
  spo2: string;
  symptoms: string;
  dob: string;
  age: string;
}

interface AilmentModuleOption {
  key?: string;
  module_key?: string;
  name: string;
  active?: boolean;
}

// The API round-trips `dob` (a DATE column) as a UTC-midnight ISO timestamp,
// so slicing that string directly grabs the UTC calendar date — off by a day
// from the intended local date whenever the browser isn't in the same
// timezone the value was stored in. Local getters correctly recover the
// calendar date the server meant, since they interpret the same instant in
// the viewer's own timezone (matching the hospital's IST clients in practice).
function toDateInputValue(dob: string | null | undefined): string {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Section completeness for the three lettered cards below — same rule as
// PreConsultStage's own statusFromCount (duplicated rather than shared; see
// this file's ageFromDob for the established precedent of not reaching for
// a common module three stages haven't yet needed together).
function statusFromCount(answered: number, total: number): SectionStatus {
  if (total === 0 || answered === 0) return 'incomplete';
  return answered === total ? 'complete' : 'partial';
}

// DOB is the more reliable source of truth once known — auto-fill age from
// it so staff don't have to compute/type both (mirrors VerifyStage's same helper).
function ageFromDob(dob: string): string | null {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? String(age) : null;
}

interface VitalsStageProps extends Pick<StageProps, 'visit' | 'patient' | 'completeStage' | 'loading'> {
  /**
   * Set only when this same component is being reused to amend an already-
   * completed Vitals stage (StageEditModal.tsx, opened from clicking the
   * Vitals bubble in the stepper) rather than recording it live. The exact
   * stage_data that stage was completed with — seeds the form instead of the
   * usual blank-plus-patient-defaults, and switches off the handful of
   * behaviors that only make sense the first time this stage runs (see the
   * three checks against it below).
   */
  amendFrom?: Record<string, any> | null;
  /**
   * StageEditModal.tsx's default, pre-"Edit" state — renders this exact
   * form, pre-filled from `amendFrom`, wrapped in a disabled `<fieldset>`
   * rather than switching to a separate, differently-laid-out summary
   * (StageDataContent). One implementation of "what this stage looks like",
   * viewed or edited, instead of two that can drift. Only meaningful
   * alongside `amendFrom` — the live pipeline never passes this.
   */
  readOnly?: boolean;
}

export default function VitalsStage({ visit, patient, completeStage, loading, amendFrom, readOnly }: VitalsStageProps) {
  const [v, setV] = useState<VitalsFormState>(() => amendFrom ? {
    bloodGroup: amendFrom.bloodGroup || '', weight: amendFrom.weight || '', height: amendFrom.height || '',
    bp_sys: amendFrom.bp_sys || '', bp_dia: amendFrom.bp_dia || '', pulse: amendFrom.pulse || '',
    temp: amendFrom.temp || '', spo2: amendFrom.spo2 || '', symptoms: amendFrom.symptoms || '',
    dob: amendFrom.dob || '', age: amendFrom.age || '',
  } : { bloodGroup:'',weight:'',height:'',bp_sys:'',bp_dia:'',pulse:'',temp:'',spo2:'',symptoms:'',dob:'',age:'' });
  const [modules, setModules]   = useState<AilmentModuleOption[]>([]);
  const [selectedModule, setSelectedModule] = useState(amendFrom?.selected_module || 'none');

  // Symptoms card — the fields Admin → Clinical → Vitals Form Customization
  // configures (features/symptom-form/symptomForm.ts has the storage model).
  // `v.symptoms` stays as the free text: what intake/voice carried in, what a
  // visit recorded before this form existed, and the whole card if the field
  // list can't be loaded — then the old single box is shown instead, so this
  // stage never loses its symptoms input.
  const snapshot = readSnapshot(amendFrom);
  const symptomFieldsQuery = useSymptomFields();
  const [symptomValues, setSymptomValues] = useState<Record<string, SymptomValue>>(() => valuesFromAnswers(snapshot?.answers));
  const [showMissing, setShowMissing] = useState(false);
  // An answer this visit recorded against a field since disabled or removed
  // is still shown (and kept on save) when amending — never silently dropped.
  const symptomFields = useMemo<SymptomField[]>(() => {
    const live = symptomFieldsQuery.data ?? [];
    const orphans = (snapshot?.answers ?? [])
      .filter(a => !live.some(f => f.field_key === a.key))
      .map((a, i): SymptomField => ({
        id: -1 - i, field_key: a.key, label: a.label, field_type: a.type,
        options: Array.isArray(a.value) ? a.value : typeof a.value === 'string' ? [a.value] : [],
        placeholder: null, help_text: null, max_length: null, allow_custom: true, mandatory: false, enabled: true,
        is_system: false, display_order: 1000 + i, updated_by: null, updated_at: '',
      }));
    return [...live, ...orphans];
  }, [symptomFieldsQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps -- snapshot comes from amendFrom, fixed for this component's lifetime
  const structured = symptomFields.length > 0;
  const setSymptom = (key: string, value: SymptomValue) => setSymptomValues(p => ({ ...p, [key]: value }));

  // Free text (carried from intake, spoken via voice fill, or recorded before
  // this form existed) goes into the form's first text box rather than
  // being lost or shown as the symptoms themselves. Nothing is inferred from
  // it — "no fever" must never tick Fever.
  const freeTextField = symptomFields.find(f => f.field_type === 'textarea') ?? symptomFields.find(f => f.field_type === 'text');
  const appendFreeText = (field: SymptomField, extra: string) => setSymptomValues(p => {
    const cur = typeof p[field.field_key] === 'string' ? (p[field.field_key] as string).trim() : '';
    return { ...p, [field.field_key]: cur ? `${cur} ${extra}` : extra };
  });
  // Live recording only. Amending, v.symptoms is either a structured visit's
  // composed text or an older visit's free text — moving the latter would
  // rewrite it ("Additional details: …") on a save that only fixed a BP.
  useEffect(() => {
    if (!freeTextField || !v.symptoms.trim() || amendFrom) return;
    appendFreeText(freeTextField, v.symptoms.trim());
    setV(p => ({ ...p, symptoms: '' }));
  }, [freeTextField?.field_key, v.symptoms]); // eslint-disable-line react-hooks/exhaustive-deps

  const symptomAnswers = structured ? buildAnswers(symptomFields, symptomValues) : [];
  const symptomsText = structured
    ? [composeSymptomsText(symptomAnswers), snapshot ? '' : v.symptoms.trim()].filter(Boolean).join(' — ')
    : v.symptoms;
  // In the fallback (no field list), an amendment keeps the structured
  // answers it was recorded with rather than erasing them.
  const symptomForm = structured
    ? (symptomAnswers.length ? { answers: symptomAnswers } : null)
    : (amendFrom?.symptom_form ?? null);
  const missingSymptomFields = structured && !amendFrom ? missingMandatory(symptomFields, symptomValues) : [];
  // Text longer than its field's character limit (Vitals Form Customization) —
  // typing stops at the limit, but voice or intake text can arrive longer.
  const tooLongSymptomFields = structured && !amendFrom ? overLimit(symptomFields, symptomValues) : [];
  // Out-of-range highlighting (lib/vitalThresholds.ts) now lives inside
  // VitalsCoreFields itself, shared with ConsultStage's correction form —
  // this file only still needs the plain bmi *value* (not the warn flags),
  // for handleSave's payload below.
  const bmi = computeBmi(v.weight, v.height);
  const set = (k: keyof VitalsFormState, val: string) => setV(p=>({...p,[k]:val}));
  const setDob = (val: string) => setV(p => {
    const computed = ageFromDob(val);
    return { ...p, dob: val, age: computed !== null ? computed : p.age };
  });
  const applyVoiceFields = (extracted: Record<string, string>) => {
    const cleaned = Object.fromEntries(Object.entries(extracted).filter(([, val]) => val !== '' && val != null));
    // Spoken symptoms go to the form's text box, not over v.symptoms.
    if (cleaned.symptoms && structured && freeTextField) {
      appendFreeText(freeTextField, cleaned.symptoms);
      delete cleaned.symptoms;
    }
    setV(p => ({ ...p, ...cleaned }));
  };

  // Blood group and height are on the patient record once captured once —
  // pre-fill instead of re-asking every visit. Weight is pre-filled from the
  // last known value too (a starting point, since it changes visit to visit)
  // but stays a plain editable input so the nurse corrects it as needed.
  // DOB/age follow the same pre-fill pattern — DOB rarely needs re-asking
  // once known, and age is shown as a starting point (re-confirmed each visit).
  // Only fills currently-empty fields, so it never clobbers something the
  // nurse already typed in this session.
  useEffect(() => {
    // Amending an already-completed stage already seeded `v` from the real
    // historical reading (see useState above) — patient-record defaults
    // would only be relevant the first time this stage is ever filled.
    if (!patient || amendFrom) return;
    setV(p => ({
      ...p,
      bloodGroup: p.bloodGroup || patient.blood_group || '',
      height: p.height || (patient.height_cm != null ? String(patient.height_cm) : ''),
      weight: p.weight || (patient.last_weight_kg != null ? String(patient.last_weight_kg) : ''),
      dob: p.dob || toDateInputValue(patient.dob),
      age: p.age || (patient.age != null ? String(patient.age) : ''),
    }));
  }, [patient]); // eslint-disable-line react-hooks/exhaustive-deps -- amendFrom is fixed for this component's lifetime, never toggles mid-mount

  // Chief complaint captured at Intake (voice, kiosk, or phone — all funnel
  // through VoiceIntake.jsx's "Start visit with this data") — carried via a
  // window global rather than a prop since it has to survive the Register
  // stage in between. Consumed once so a later, different patient's Vitals
  // stage in the same tab never picks up a stale complaint.
  useEffect(() => {
    if (amendFrom) return; // one-time global belonging to this visit's original intake, not to an unrelated later edit
    const carried = window.__voiceIntakeComplaints;
    if (carried) {
      setV(p => ({ ...p, symptoms: p.symptoms || carried }));
      window.__voiceIntakeComplaints = null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- amendFrom is fixed for this component's lifetime, never toggles mid-mount

  // Admin-configured Assessment Modules (Diabetes, Fever, Heart...) — the
  // nurse picks at most one here, which drives whether/what shows up next
  // at Pre-Consult. Selecting 'none' skips Pre-Consult entirely.
  useEffect(() => {
    getAilmentTemplates()
      .then((list: AilmentModuleOption[]) => setModules(list.filter(t => t.active && t.module_key)))
      .catch(() => setModules([]));
  }, []);

  const handleSave = async () => {
    // Mandatory symptom fields gate only the first recording — an amendment
    // to a visit recorded before a field became mandatory must stay possible.
    if (missingSymptomFields.length || tooLongSymptomFields.length) { setShowMissing(true); return; }
    const data = { ...v, symptoms: symptomsText, symptom_form: symptomForm, bmi, selected_module: selectedModule };
    // This INSERTs a new `vitals` history row — correct the first time this
    // stage is ever completed, but amending it later must never insert a
    // second one (that would fabricate a duplicate clinical event on the
    // patient's trend timeline). StageEditModal.tsx's amend flow already
    // updates the one real row for this visit via the amend endpoint.
    if (visit?.patient_id && !amendFrom) {
      await authFetch('/api/patients/vitals/save', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          patient_id: visit.patient_id, visit_id: visit.id,
          blood_group: v.bloodGroup, weight_kg: v.weight || null, height_cm: v.height || null,
          bmi: bmi || null, bp_systolic: v.bp_sys || null, bp_diastolic: v.bp_dia || null,
          pulse_rate: v.pulse || null, temperature: v.temp || null, spo2: v.spo2 || null,
          symptoms: symptomsText, dob: v.dob || null, age: v.age || null,
        }),
      }).catch(()=>{});
    }
    completeStage('vitals', data);
  };

  // Nothing here is actually required by the backend (see handleSave) — this
  // states whether anything has been entered yet, not a validation gate, so
  // the "Save vitals & continue" button below stays exactly as permissive as
  // it always was.
  const anyVitalFilled = [v.bp_sys, v.bp_dia, v.pulse, v.temp, v.spo2, v.weight, v.height].some(Boolean);

  // Three lettered sections, in the order the assessment's own P1 finding
  // asked for: the readings that are "why the stage exists" (BP, pulse,
  // temp, SpO2, weight, height — BMI is derived, not answered, so it's
  // excluded from the count) come first; DOB/age/blood group — "demographic
  // backfill", per the same finding — are demoted to a smaller card after
  // it, instead of sitting in the same equally-weighted row of ten tiles.
  const vitalsAnswered = [v.bp_sys, v.bp_dia, v.pulse, v.temp, v.spo2, v.weight, v.height].filter(Boolean).length;
  const vitalsStatus = statusFromCount(vitalsAnswered, 7);
  const identityAnswered = [v.dob, v.age, v.bloodGroup].filter(Boolean).length;
  const identityStatus = statusFromCount(identityAnswered, 3);
  // Complete once something is recorded and nothing mandatory is blank —
  // not "every field answered": most visits rightly leave some blank.
  const symptomsAnswered = structured
    ? symptomFields.filter(f => isAnswered(f.field_type, symptomValues[f.field_key])).length
    : (v.symptoms.trim() ? 1 : 0);
  const symptomsStatus: SectionStatus = symptomsAnswered === 0 ? 'incomplete'
    : missingSymptomFields.length || tooLongSymptomFields.length ? 'partial' : 'complete';

  return (
    // One shared mx-auto max-w-[1560px] shell for the whole stage — matching
    // PreConsultStage.tsx's own top-level wrapper — rather than wrapping just
    // the header in its own `StageLayout variant="single"` (680px). Two
    // independently-centered boxes of different widths, as siblings, center
    // at different left edges: the 680px header sat ~440px further right
    // than the wider grid below it on a wide screen, reading as the title/
    // patient-line/voice-banner all "hanging toward the right" relative to
    // the section cards. The header, and every section below, are now plain
    // full-width blocks sharing this one shell.
    <div className="fade-in mx-auto flex w-full max-w-[1560px] flex-col gap-3.5">
    {/* `contents` so the fieldset generates no box of its own (a bare
        fieldset defaults to `min-width: min-content`, which would fight the
        grid/flex layouts below it) — disabling reaches every descendant
        input/select/textarea/button regardless, per the HTML spec, so the
        read-only view (readOnly=true) renders identically to the editable
        one, just inert, rather than a different summary layout. */}
    <fieldset disabled={readOnly} className="contents border-0 p-0 m-0">
    <div>
      <div style={{ fontSize:20, fontWeight:700, marginBottom:6 }}>❤️ Record vitals</div>
      {/* Patient name/visit id line removed (2026-09-13) — this stage always
          renders under VisitWorkflow's own PatientBanner (live) or
          StageEditModal's header (amend mode), both of which already carry
          it; repeating it here just cost vertical space. */}
      <VoiceFormFiller fields={VOICE_FIELDS} onFields={applyVoiceFields} label="🎙 Fill vitals by voice" />
    </div>

    {/* Two columns: Vitals with Identity stacked under it on the left,
        Symptoms on its own on the right. Vitals' fields fill only part of
        its card's height, and the Symptoms form (several configurable
        fields since Vitals Form Customization) is the tall one — so Identity sits
        in the space under Vitals instead of pushing Symptoms down. Same
        max-w-[1560px] shell as the header above, `items-start` so the two
        columns don't stretch to match each other's height. */}
    <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2 items-start">
      <div className="flex flex-col gap-3.5">
        <SectionCard index="A" title="Vitals" status={vitalsStatus}>
          <VitalsCoreFields value={v} onChange={set} />
        </SectionCard>

        <SectionCard index="B" title="Identity" status={identityStatus}>
          <div className="flex flex-wrap gap-2.5">
            <StatField icon="🎂" label="Date of birth">
              <input type="date" className="w-full" value={v.dob} onChange={e=>setDob(e.target.value)} />
            </StatField>
            <StatField icon="🔢" label="Age (years)">
              <input type="number" className="w-full" value={v.age} onChange={e=>set('age',e.target.value)} />
            </StatField>
            <StatField icon="🩸" label="Blood group">
              <select className="w-full" value={v.bloodGroup} onChange={e=>set('bloodGroup',e.target.value)}>
                {['','A+','A-','B+','B-','O+','O-','AB+','AB-'].map(b=><option key={b} value={b}>{b||'Select'}</option>)}
              </select>
            </StatField>
          </div>
        </SectionCard>
      </div>

      <SectionCard index="C" title="Symptoms" status={symptomsStatus}>
        {structured ? (
          <>
            <SymptomFormFields
              fields={symptomFields}
              values={symptomValues}
              onChange={setSymptom}
              missing={showMissing ? new Set(missingSymptomFields.map(f => f.field_key)) : undefined}
              disabled={readOnly}
            />
            {/* An older visit's free text, kept as written and still editable. */}
            {amendFrom && !snapshot && (amendFrom.symptoms || v.symptoms) ? (
              <div className="mt-3">
                <label className="lbl">Recorded before structured symptoms</label>
                <textarea style={{resize:'vertical',height:60}} value={v.symptoms} onChange={e=>set('symptoms',e.target.value)} />
              </div>
            ) : !snapshot && v.symptoms.trim() ? (
              // Only when the form has no text box to carry it.
              <div className="mt-2 text-[12px] text-ink-muted">Also recorded: “{v.symptoms.trim()}”</div>
            ) : null}
          </>
        ) : symptomFieldsQuery.isLoading ? (
          <div className="text-[12px] text-ink-muted">Loading symptom fields…</div>
        ) : (
          <>
            <label className="lbl" style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span>Symptoms / complaints</span>
              <VoiceMicButton onText={t => set('symptoms', (v.symptoms ? v.symptoms + ' ' : '') + t)} />
            </label>
            <textarea style={{resize:'vertical',height:72}} value={v.symptoms} onChange={e=>set('symptoms',e.target.value)} />
          </>
        )}
      </SectionCard>
    </div>

    {/* Patient history (diagnoses/procedures/medications) and its AI "Pull
        from patient record" moved to Pre-Consult (KMC #27/#28) — this stage
        records the current presentation only. A visit that skips Pre-Consult
        ('None' below) has it captured in Consultation's History tab instead. */}

    {/* This decision routes the rest of the pipeline (whether/which Pre-
        Consult checklist runs next) — the amend endpoint has no way to
        re-route a visit that has already moved past that point, so changing
        it here after the fact would silently do nothing. Shown only the
        first time this stage is filled. */}
    {!amendFrom && (
      <SectionCard title="Next step">
        <label className="lbl">Which assessment does this visit need?</label>
        <div style={{ marginTop:4 }}>
          <SegmentedControl
            options={[{ key:'none', name:'None — go straight to Consultant' }, ...modules, { key:'other', name:'Other / general triage' }]
              .map(m => ({ value: (m.module_key || m.key)!, label: m.name }))}
            value={selectedModule}
            onChange={setSelectedModule}
          />
        </div>
        <div style={{ fontSize:11.5, color:'#6B7280', marginTop:6 }}>
          {selectedModule === 'none'
            ? 'Patient history and current medication will be recorded by the doctor in Consultation.'
            : 'Patient history and current medication are recorded at Pre-Consult.'}
        </div>
      </SectionCard>
    )}

    <StageActionBar
      readiness={readOnly ? 'Viewing a previously recorded reading — click Edit to correct it' : amendFrom ? 'Correcting a previously recorded reading' : showMissing && missingSymptomFields.length ? `Required: ${missingSymptomFields.map(f => f.label).join(', ')}` : showMissing && tooLongSymptomFields.length ? `Too long: ${tooLongSymptomFields.map(f => `${f.label} (max ${f.max_length ?? 1000})`).join(', ')}` : anyVitalFilled ? '✓ Ready to save' : 'No vitals entered yet — you can still continue'}
      readinessTone={readOnly || amendFrom ? 'neutral' : showMissing && (missingSymptomFields.length || tooLongSymptomFields.length) ? 'warn' : anyVitalFilled ? 'success' : 'warn'}
    >
      <button className="btn-primary" onClick={handleSave} disabled={loading}>
        {amendFrom ? (loading ? 'Saving…' : '💾 Save correction') : 'Save vitals & continue →'}
      </button>
    </StageActionBar>
    </fieldset>
    </div>
  );
}
